import { Action } from 'redux';
import {
    ChannelEvent,
    DeliveryLogType,
    EventLog,
    LinkContacts,
    LinksClicked,
    Log,
    LogSummary,
    MessageCategory,
    ReportLocationStats,
    SortStatsBy,
    StatusMessageCategory,
    TemplateLink,
    TrackedLink,
} from '@dev/ee-api/ee-api-models';
import { ReactText } from 'react';

import {
    ExportEventsParams,
    ExportLinkContactsParams,
    ExportLinksParams,
    LinkContactsParams,
    ReportsSummaryParams,
    ResendParams,
} from '@/services/ElasticApi';

import { SingleContactPaginationInterface } from '../contacts/types';
import { CampaignsActionTypes } from '../campaigns/types';
import { DeviceDetailsInterface } from './deviceDetails/types';

export interface UrlSearchParams {
    page?: number;
    perPage?: number;
    from?: string;
    to?: string;
}

export interface UrlSearchEventsActiveParams {
    page?: number;
    perPage?: number;
    from?: string;
    to?: string;
    lastEvent?: boolean;
    events?: DeliveryLogType[];
    result?: StatusMessageCategory[];
    search?: string;
}

export interface UrlSearchInProgressActiveParams {
    page?: number;
    perPage?: number;
    from?: string;
    to?: string;
    statuses?: DeliveryLogType[];
    result?: StatusMessageCategory[];
    search?: string;
}
export interface PaginationInterface {
    activePage: number;
    perPage: number;
    to?: string;
    from?: string;
}
export interface LogParams {
    statuses?: DeliveryLogType[];
    from?: string;
    to?: string;
    channelName?: string;
    limit?: number;
    offset?: number;
    includeEmail?: boolean;
    includeSMS?: boolean;
    email?: string;
    ipaddress?: string;
    messageCategory?: MessageCategory[];
    infinityScroll?: boolean;
}

export interface LinkTrackingAbSplit {
    loading: boolean;
    collection?: TemplateLink[];
    totalCount?: number;
    offset?: number;
    limit?: number;
}

export interface ReportsState {
    linkTrackingList?: TrackedLink[];
    log?: Log;
    resendLoading?: boolean;
    eventLog?: EventLog;
    eventLogLoading?: boolean;
    eventLogParams?: EventLogParams;
    eventLogParamsByChannelName?: EventLogParamsByChannelName;
    eventLogByChannelNameLoading?: boolean;
    eventsCount?: EventLogTotalEventCounts;
    resultsCount?: EventLogTotalResultCounts;
    eventsByChannelNameCount?: any;
    resultsByChannelNameCount?: EventLogTotalResultCounts;
    eventsCounts?: Record<string, ReactText>;
    countsLoading?: boolean;
    eventLogByChannelName?: ChannelEvent[];
    limit?: number;
    offset?: number;
    logsLoading?: boolean;
    linkTrackingLoading?: boolean;
    linkTrackingAbSplit?: LinkTrackingAbSplit;
    isLoadingLinks?: boolean;
    links?: LinksClicked[];
    linkCounts?: number;
    logsCount?: number;
    statuses?: DeliveryLogType[];
    reportsSummaryLoading?: boolean;
    reportsSummary?: LogSummary;
    linkTrackingActivePage?: number;
    linkTrackingPerPage?: number;
    linkContactsPagination?: PaginationInterface;
    totalLinkContactsCount?: number;
    linkContactsLoading?: boolean;
    linkContactsAllLoading?: boolean;
    linkContactsList?: LinkContacts;
    linkContactsAllList?: LinkContacts;
    locationStats?: {
        dataSet?: ReportLocationStats[];
        loading?: boolean;
        totalCount?: number;
    };
    deviceDetails?: DeviceDetailsInterface;
}
export interface SingleContactLog {
    date: string;
    subject: string;
    status: string;
    from: string;
    msgID: string;
    key: string;
    id: string;
}

export interface LocationReportsInterface {
    channelName: string;
    from?: string;
    to?: string;
    limit?: number;
    offset?: number;
    sortBy?: SortStatsBy;
    sortDescending?: boolean;
    isDisabledAddingPaginationParams?: boolean;
}

export interface ReportsInProgressInterface {
    channelName: string;
}

export enum ReportsActionTypes {
    FETCH_START = '@@reports/FETCH_START',
    FETCH_ERROR = '@@reports/FETCH_ERROR',
    CLEAR_CAMPAIGN_LINK_TRACKING = '@@reports/CLEAR_CAMPAIGN_LINK_TRACKING',
    CLEAR_CAMPAIGN_REPORTS_DATA = '@@reports/CLEAR_CAMPAIGN_REPORTS_DATA',
    FETCH_SUCCESS = '@@reports/FETCH_SUCCESS',
    EVENT_LOG_START = '@@reports/EVENT_LOG_START',
    EVENT_LOG_SUCCESS = '@@reports/EVENT_LOG_SUCCESS',
    EVENT_LOG_ERROR = '@@reports/EVENT_LOG_ERROR',
    EVENT_LOG_BY_CHANNELNAME_START = '@@reports/EVENT_LOG_BY_CHANNELNAME_START',
    EVENT_LOG_BY_CHANNELNAME_SUCCESS = '@@reports/EVENT_LOG_BY_CHANNELNAME_SUCCESS',
    EVENT_LOG_BY_CHANNELNAME_ERROR = '@@reports/EVENT_LOG_BY_CHANNELNAME_ERROR',
    FETCH_CONTACT_LINKS_START = '@@reports/FETCH_CONTACT_LINKS_START',
    FETCH_CONTACT_LINKS_ERROR = '@@reports/FETCH_CONTACT_LINKS_ERROR',
    FETCH_CONTACT_LINKS_SUCCESS = '@@reports/FETCH_CONTACT_LINKS_SUCCESS',
    GET_REPORTS_SUMMARY_START = '@@reports/GET_REPORTS_SUMMARY_START',
    GET_REPORTS_SUMMARY_SUCCESS = '@@reports/GET_REPORTS_SUMMARY_SUCCESS',
    GET_REPORTS_SUMMARY_ERROR = '@@reports/GET_REPORTS_SUMMARY_ERROR',
    LOAD_LINK_TRACKING_START = '@@reports/LOAD_LINK_TRACKING_START',
    LOAD_LINK_TRACKING_SUCCESS = '@@reports/LOAD_LINK_TRACKING_SUCCESS',
    LOAD_LINK_TRACKING_ERROR = '@@reports/LOAD_LINK_TRACKING_ERROR',
    EXPORT_LINKS = '@@reports/EXPORT_LINKS',
    LOAD_LINK_TRACKING_ABSPLIT_START = '@@reports/LOAD_LINK_TRACKING_ABSPLIT_START',
    LOAD_LINK_TRACKING_ABSPLIT_SUCCESS = '@@reports/LOAD_LINK_TRACKING_ABSPLIT_SUCCESS',
    LOAD_LINK_TRACKING_ABSPLIT_ERROR = '@@reports/LOAD_LINK_TRACKING_ABSPLIT_ERROR',
    LINK_CONTACTS_START = '@@reports/LINK_CONTACTS_START',
    LINK_CONTACTS_SUCCESS = '@@reports/LINK_CONTACTS_SUCCESS',
    LINK_CONTACTS_ERROR = '@@reports/LINK_CONTACTS_ERROR',
    LINK_CONTACTS_ALL_START = '@@reports/LINK_CONTACTS_ALL_START',
    LINK_CONTACTS_ALL_SUCCESS = '@@reports/LINK_CONTACTS_ALL_SUCCESS',
    LINK_CONTACTS_ALL_ERROR = '@@reports/LINK_CONTACTS_ALL_ERROR',
    EXPORT_LINK_CONTACTS = '@@reports/EXPORT_LINK_CONTACTS',
    GET_REPORTS_LOCATION_STATS_BY_NAME_START = '@@reports/GET_REPORTS_LOCATION_STATS_BY_NAME_START',
    GET_REPORTS_LOCATION_STATS_BY_NAME_SUCCESS = '@@reports/GET_REPORTS_LOCATION_STATS_BY_NAME_SUCCESS',
    CLEAR_LOG = '@@reports/CLEAR_LOG',
    EXPORT_EVENTS = '@@reports/EXPORT_EVENTS',
    RESEND_START = '@@reports/RESEND_START',
    RESEND_SUCCESS = '@@reports/RESEND_SUCCESS',
    RESEND_ERROR = '@@reports/RESEND_ERROR',
    EXPORT_LINKS_START = '@@reports/EXPORT_LINKS_START',
    FETCH_CHANNEL_EVENTS_COUNTS = '@@reports/FETCH_CHANNEL_EVENTS_COUNTS',
    UPDATE_CHANNEL_EVENT_COUNTS = '@@reports/UPDATE_CHANNEL_EVENT_COUNTS',
    GET_DEVICE_DETAILS = '@@reports/GET_DEVICE_DETAILS',
    GET_DEVICE_DETAILS_SUCCESS = '@@reports/GET_DEVICE_DETAILS_SUCCESS',
    GET_DEVICE_DETAILS_ERROR = '@@reports/GET_DEVICE_DETAILS_ERROR',
    GET_DEVICE_DETAILS_CAMPAIGNS = '@@reports/GET_DEVICE_DETAILS_CAMPAIGNS',
    GET_DEVICE_DETAILS_CAMPAIGNS_SUCCESS = '@@reports/GET_DEVICE_DETAILS_CAMPAIGNS_SUCCESS',
    GET_DEVICE_DETAILS_CAMPAIGNS_ERROR = '@@reports/GET_DEVICE_DETAILS_CAMPAIGNS_ERROR',
}

export interface ReportsFetchStartAction extends Action {
    type: ReportsActionTypes.FETCH_START;
    payload: {
        params: LogParams;
        logsLoading: boolean;
        statuses: DeliveryLogType[];
    };
}

export interface ReportsFetchErrorAction extends Action {
    type: ReportsActionTypes.FETCH_ERROR;
    payload: {
        logsLoading: boolean;
    };
}

export interface ClearCampaignsReportsSummaryAction extends Action {
    type: CampaignsActionTypes.CLEAR_CAMPAIGN_REPORTS_SUMMARY;
}

export interface ClearCampaignsLinkTrackingAction extends Action {
    type: ReportsActionTypes.CLEAR_CAMPAIGN_LINK_TRACKING;
}
export interface ClearCampaignsReportsDataAction extends Action {
    type: ReportsActionTypes.CLEAR_CAMPAIGN_REPORTS_DATA;
}
export interface ReportsFetchSuccessAction extends Action {
    type: ReportsActionTypes.FETCH_SUCCESS;
    payload: {
        log: Log;
        logsCount: number;
        logsLoading: boolean;
        statuses: DeliveryLogType[];
        hasMoreLogs: boolean;
    };
}

export interface EventLogParams {
    statuses: DeliveryLogType[];
    from?: string;
    to?: string;
    channelName?: string;
    limit?: number;
    offset?: number;
    includeEmail?: boolean;
    includeSMS?: boolean;
    email?: string;
    ipaddress?: string;
    messageCategory?: StatusMessageCategory[];
    lastEvent?: boolean;
}

export interface EventLogTotalResultCounts {
    total: number;
    allResultCount: number;
    accountproblem: number;
    blacklisted: number;
    codeerror: number;
    connectionproblem: number;
    connectionterminated: number;
    dnsproblem: number;
    error: number;
    greylisted: number;
    ignore: number;
    manualcancel: number;
    nomailbox: number;
    notdelivered: number;
    notdeliveredcancelled: number;
    spam: number;
    spfproblem: number;
    throttled: number;
    timeout: number;
    unknown: number;
}

export interface EventLogTotalEventCounts {
    sending: number;
    readytosend: number;
    waitingtoretry: number;
    error?: number;
    sent?: number;
    opened?: number;
    clicked?: number;
    unsubscribed?: number;
    abuseReport?: number;
    suppressed?: number;
    all?: number;
}

export interface EventLogStartAction extends Action {
    type: ReportsActionTypes.EVENT_LOG_START;
    payload: {
        params: EventLogParams;
        eventLogLoading: boolean;
    };
}

export interface EventLogSuccessAction extends Action {
    type: ReportsActionTypes.EVENT_LOG_SUCCESS;
    payload: {
        eventLog: EventLog;
        eventsCount: EventLogTotalEventCounts;
        resultsCount: EventLogTotalResultCounts;
        eventLogLoading: boolean;
        eventLogParams: EventLogParams;
    };
}

export interface EventLogErrorAction extends Action {
    type: ReportsActionTypes.EVENT_LOG_ERROR;
    payload: {
        eventLogLoading: boolean;
    };
}

export interface EventLogParamsByChannelName {
    channelName: string;
    statuses: DeliveryLogType[];
    messageCategory?: StatusMessageCategory[];
    from?: string;
    to?: string;
    email?: string;
    lastEvent?: boolean;
    limit?: number;
    offset?: number;
    useMessageCategoryTypeInHeaders?: boolean;
}
export interface EventLogByChannelNameStartAction extends Action {
    type: ReportsActionTypes.EVENT_LOG_BY_CHANNELNAME_START;
    payload: {
        params: EventLogParamsByChannelName;
        eventLogByChannelNameLoading: boolean;
    };
}

export interface EventLogByChannelNameSuccessAction extends Action {
    type: ReportsActionTypes.EVENT_LOG_BY_CHANNELNAME_SUCCESS;
    payload: {
        eventLogByChannelName: ChannelEvent[];
        eventsByChannelNameCount: number;
        resultsByChannelNameCount: EventLogTotalResultCounts;
        eventLogByChannelNameLoading: boolean;
        eventLogParamsByChannelName: EventLogParamsByChannelName;
    };
}

export interface EventLogByChannelNameErrorAction extends Action {
    type: ReportsActionTypes.EVENT_LOG_BY_CHANNELNAME_ERROR;
    payload: {
        eventLogByChannelNameLoading: boolean;
    };
}

export interface ContactLinksFetchStartAction extends Action {
    type: ReportsActionTypes.FETCH_CONTACT_LINKS_START;
    payload: {
        params: SingleContactPaginationInterface;
        isLoadingLinks: boolean;
    };
}

export interface ContactLinksFetchErrorAction extends Action {
    type: ReportsActionTypes.FETCH_CONTACT_LINKS_ERROR;
    payload: {
        isLoadingLinks: boolean;
    };
}

export interface ContactLinksFetchSuccessAction extends Action {
    type: ReportsActionTypes.FETCH_CONTACT_LINKS_SUCCESS;
    payload: {
        links: LinksClicked[];
        isLoadingLinks: boolean;
        linkCounts: number;
    };
}

export interface GetReportsSummaryInterface extends ReportsSummaryParams {
    reportsSummaryLoading?: boolean;
}
export interface GetReportsSummaryStartAction extends Action {
    type: ReportsActionTypes.GET_REPORTS_SUMMARY_START;
    payload: GetReportsSummaryInterface;
}
export interface GetReportsSummarySuccessAction extends Action {
    type: ReportsActionTypes.GET_REPORTS_SUMMARY_SUCCESS;
    payload: {
        reportsSummary: LogSummary;
        reportsSummaryLoading: boolean;
    };
}
export interface GetReportsSummaryErrorAction extends Action {
    type: ReportsActionTypes.GET_REPORTS_SUMMARY_ERROR;
    payload: {
        reportsSummaryLoading: boolean;
    };
}

export interface GetLinkTrackingListStartAction extends Action {
    type: ReportsActionTypes.LOAD_LINK_TRACKING_START;
    payload: {
        activePage?: number;
        linkSearch?: string;
        channelName: string;
        from?: string;
        to?: string;
        perPage?: number;
        /**
         * if false/ not passed - from, to, activePage and perPage will be ignored
         */
        isRequiredFullDateRange?: boolean;
    };
}
export interface GetLinkTrackingListSuccessAction extends Action {
    type: ReportsActionTypes.LOAD_LINK_TRACKING_SUCCESS;
    payload: {
        linkTrackingLoading: boolean;
        linkTrackingList: TrackedLink[];
        totalLinkCount?: number;
        linkTrackingActivePage?: number;
        linkTrackingPerPage?: number;
    };
}
export interface GetLinkTrackingLisErrorAction extends Action {
    type: ReportsActionTypes.LOAD_LINK_TRACKING_ERROR;
    payload: {
        linkTrackingLoading: boolean;
    };
}

export interface ExportLinksAction extends Action {
    type: ReportsActionTypes.EXPORT_LINKS;
    payload: ExportLinksParams;
}

export type LinktrackingsAbsplitActionParams = {
    channelName: string;
    linkSearch?: string;
    from?: string;
    to?: string;
    perPage?: number;
    activePage?: number;
    isUpdateUrlRequired?: boolean;
};

export interface GetLinkTrackingAbSplitStartAction extends Action {
    type: ReportsActionTypes.LOAD_LINK_TRACKING_ABSPLIT_START;
    payload: {
        channelName: string;
        linkSearch?: string;
        from?: string;
        to?: string;
        perPage?: number;
        activePage?: number;
        isUpdateUrlRequired?: boolean;
    };
}
export interface GetLinkTrackingAbSplitSuccessAction extends Action {
    type: ReportsActionTypes.LOAD_LINK_TRACKING_ABSPLIT_SUCCESS;
    payload: {
        loading: boolean;
        collection: TrackedLink[];
        totalCount?: number;
        offset?: number;
        limit?: number;
    };
}
export interface GetLinkTrackingAbSplitErrorAction extends Action {
    type: ReportsActionTypes.LOAD_LINK_TRACKING_ABSPLIT_ERROR;
    payload: {
        loading: boolean;
    };
}

export type CreateListOptions = {
    list?: string;
    newList?: boolean;
};

export interface LinkContactsState {
    exportOptions: ExportLinkContactsParams;
    createListOptions: CreateListOptions;
    campaignDateToSendOn?: string;
}

export interface LinkContactsStartAction extends Action {
    type: ReportsActionTypes.LINK_CONTACTS_START;
    payload: LinkContactsParams;
}
export interface LinkContactsSuccessAction extends Action {
    type: ReportsActionTypes.LINK_CONTACTS_SUCCESS;
    payload: {
        linkContactsLoading: boolean;
        linkContactsList: LinkContacts;
        totalLinkContactsCount?: number;
        linkContactsPagination: PaginationInterface;
    };
}
export interface LinkContactsErrorAction extends Action {
    type: ReportsActionTypes.LINK_CONTACTS_ERROR;
    payload: {
        linkContactsLoading: boolean;
    };
}

export interface LinkContactsAllStartAction extends Action {
    type: ReportsActionTypes.LINK_CONTACTS_ALL_START;
    payload: LinkContactsParams;
}
export interface LinkContactsAllSuccessAction extends Action {
    type: ReportsActionTypes.LINK_CONTACTS_ALL_SUCCESS;
    payload: {
        linkContactsAllList: LinkContacts;
    };
}
export interface LinkContactsAllErrorAction extends Action {
    type: ReportsActionTypes.LINK_CONTACTS_ALL_ERROR;
}

export interface ExportLinkContactsAction extends Action {
    type: ReportsActionTypes.EXPORT_LINK_CONTACTS;
    payload: ExportLinkContactsParams;
}
export interface GetReportsLocationStatsByNameStartAction extends Action {
    type: ReportsActionTypes.GET_REPORTS_LOCATION_STATS_BY_NAME_START;
    payload: LocationReportsInterface;
}
export interface GetReportsLocationStatsByNameSuccessAction extends Action {
    type: ReportsActionTypes.GET_REPORTS_LOCATION_STATS_BY_NAME_SUCCESS;
    payload: {
        locationStatsData: ReportLocationStats;
    };
}
export interface ClearLogFromReportsAction extends Action {
    type: ReportsActionTypes.CLEAR_LOG;
}

export interface ExportEventsAction extends Action {
    type: ReportsActionTypes.EXPORT_EVENTS;
    payload: ExportEventsParams;
}

export interface ResendStartAction extends Action {
    type: ReportsActionTypes.RESEND_START;
    payload: {
        params: ResendParams;
    };
}

export interface ResendSuccessAction extends Action {
    type: ReportsActionTypes.RESEND_SUCCESS;
}

export interface ResendErrorAction extends Action {
    type: ReportsActionTypes.RESEND_ERROR;
}

export interface FetchChannelEventsCountsAction extends Action {
    type: ReportsActionTypes.FETCH_CHANNEL_EVENTS_COUNTS;
    payload: EventLogParamsByChannelName;
}

export interface UpdateChannelEventsCountsAction extends Action {
    type: ReportsActionTypes.UPDATE_CHANNEL_EVENT_COUNTS;
    payload: Record<string, any>;
}
